package com.arena_web.ui.components.events

import androidx.compose.runtime.*
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Input
import org.w3c.dom.HTMLInputElement

/**
 * Create Team popup — Figma node 8622:386601.
 *
 * Compact dark-blue card with title + subtitle, a single Team Name input,
 * six circular team-colour swatches, and Cancel / Create Team pill actions.
 * Each colour swatch reuses the team-card texture+glow assets so it reads
 * as a miniature team card masked into a 45px circle, with a people glyph
 * centred on top.
 */
@Composable
fun CreateTeamPopup(
    isOpen: Boolean,
    onClose: () -> Unit,
    onCreated: (name: String, color: String?) -> Unit,
    // Colours already taken by existing teams — excluded from the palette so
    // two teams can't share a colour.
    usedColors: List<String> = emptyList(),
) {
    if (!isOpen) return

    val t = LocalAppStrings.current
    val teamColors = listOf(
        "#050505", // near-black
        "#290001", // dark red
        "#0D184A", // dark blue
        "#001624", // teal
        "#A07B00", // gold
        "#B9BEB9", // light gray
    )
    // Drop colours already in use (case-insensitive) so each team is distinct.
    val takenColors = usedColors.map { it.trim().lowercase() }.toSet()
    val availableColors = teamColors.filter { it.lowercase() !in takenColors }

    var name by remember { mutableStateOf("") }
    // Pre-select the first available colour by default.
    var selectedColor by remember { mutableStateOf<String?>(availableColors.firstOrNull()) }
    var nameError by remember { mutableStateOf<String?>(null) }

    val submit: () -> Unit = {
        if (name.isBlank()) {
            nameError = t.eventTeamNameRequired
        } else {
            onCreated(name.trim(), selectedColor)
        }
    }

    // ── Mobile: single-column bottom sheet (Figma 8854:356211) ──
    if (isMobile()) {
        BottomSheet(onDismiss = onClose, contentGap = 24) {
            // Title block — title + subtitle, then a divider that fades out to
            // both sides (Figma 8854:356217).
            Column(modifier = Modifier.fillMaxWidth().sGap(16)) {
                Column(modifier = Modifier.fillMaxWidth().sGap(8)) {
                    SpanText(
                        t.eventCreateTeam,
                        modifier = Modifier
                            .sFontSize(24).sLineHeight(32).sLetterSpacing(0.24)
                            .fontWeight(FontWeight.Medium)
                            .color(Color("var(--arena-text-2)"))
                            .styleModifier { property("font-family", "Urbanist, sans-serif") }
                    )
                    SpanText(
                        t.eventCreateTeamSubtitle,
                        modifier = Modifier
                            .sFontSize(16).sLetterSpacing(0.16)
                            .color(Color("var(--arena-text-muted)"))
                            .styleModifier {
                                property("font-family", "Urbanist, sans-serif")
                                property("line-height", "1.5")
                            }
                    )
                }
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sHeight(1)
                        .styleModifier {
                            property(
                                "background",
                                "linear-gradient(90deg, rgba(148, 149, 152, 0) 0%, rgba(148, 149, 152, 0.3) 50%, rgba(148, 149, 152, 0) 100%)",
                            )
                        }
                )
            }

            // Team Name field
            Column(modifier = Modifier.fillMaxWidth().sGap(6)) {
                SpanText(
                    t.eventTeamNameLabel,
                    modifier = Modifier
                        .sPaddingLeft(4).sFontSize(14).sLineHeight(22).sLetterSpacing(0.028)
                        .color(Color("var(--arena-text-2)"))
                        .styleModifier { property("font-family", "Urbanist, sans-serif") }
                )
                TeamNameInput(
                    value = name,
                    onValueChange = { name = it; nameError = null },
                    hasError = nameError != null,
                )
                nameError?.let {
                    SpanText(
                        it,
                        modifier = Modifier.sPaddingLeft(4).sFontSize(12).color(Color("#FF6B6B"))
                            .styleModifier { property("font-family", "Urbanist, sans-serif") }
                    )
                }
            }

            // Team Color
            Column(modifier = Modifier.fillMaxWidth().sGap(18)) {
                Column(modifier = Modifier.sGap(6)) {
                    SpanText(
                        t.eventTeamColorLabel,
                        modifier = Modifier
                            .sPaddingLeft(4).sFontSize(16).sLetterSpacing(0.16)
                            .color(Color("var(--arena-text-2)"))
                            .styleModifier {
                                property("font-family", "Urbanist, sans-serif")
                                property("line-height", "1.5")
                            }
                    )
                    SpanText(
                        t.eventTeamColorSubtitle,
                        modifier = Modifier
                            .sPaddingLeft(4).sFontSize(12).sLineHeight(12).sLetterSpacing(0.012)
                            .color(Color("var(--arena-text-muted)"))
                            .styleModifier { property("font-family", "Urbanist, sans-serif") }
                    )
                }
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    availableColors.forEach { hex ->
                        ColorSwatch(
                            color = hex,
                            isSelected = selectedColor == hex,
                            onClick = { selectedColor = if (selectedColor == hex) null else hex },
                        )
                    }
                }
            }

            // Action buttons — full width, side by side.
            Row(
                modifier = Modifier.fillMaxWidth().sGap(12),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(modifier = Modifier.flexGrow(1)) {
                    PillButton(
                        text = t.commonCancel,
                        onClick = { onClose() },
                        background = AppGradients.InteractiveSecondary,
                        textColor = "#BED6F5",
                        fullWidth = true,
                    )
                }
                Box(modifier = Modifier.flexGrow(1)) {
                    PillButton(
                        text = t.eventCreateTeam,
                        onClick = submit,
                        background = "#0061FF",
                        textColor = "#FFFFFF",
                        fullWidth = true,
                    )
                }
            }
        }
        return
    }

    // Overlay
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .backgroundColor(Color("rgba(0, 0, 0, 0.6)"))
            .onClick { onClose() }
            .animation(
                PopupOverlayFadeIn.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        // Popup card — Figma 8622:386601
        Column(
            modifier = Modifier
                .sMaxWidth(374)
                .fillMaxWidth()
                .sMargin(16)
                .sBorderRadius(26)
                .sPaddingTop(22)
                .sPaddingLeft(22)
                .sPaddingRight(22)
                .sPaddingBottom(32)
                .sGap(40)
                .onClick { it.stopPropagation() }
                .animation(
                    PopupSlideUp.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
                .styleModifier {
                    property("background-color", "var(--l-s2, #09142d)")
                    property("box-shadow", "0 24px 64px rgba(0, 0, 0, 0.5)")
                }
        ) {
            // ── Header + form ─────────────────────────────────────────────
            Column(modifier = Modifier.fillMaxWidth().sGap(24)) {
                // Title block — Figma 8622:386605
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sPaddingBottom(16)
                        .sGap(16)
                        .styleModifier {
                            property("border-bottom", "1px solid rgba(148, 149, 152, 0.15)")
                        }
                ) {
                    SpanText(
                        t.eventCreateTeam,
                        modifier = Modifier
                            .sFontSize(24)
                            .sLineHeight(32)
                            .sLetterSpacing(0.24)
                            .fontWeight(FontWeight.Medium)
                            .color(Color("var(--arena-text-2)"))
                            .styleModifier {
                                property("font-family", "Urbanist, sans-serif")
                            }
                    )
                    SpanText(
                        t.eventCreateTeamSubtitle,
                        modifier = Modifier
                            .sFontSize(16)
                            .sLetterSpacing(0.16)
                            .color(Color("var(--arena-text-muted)"))
                            .styleModifier {
                                property("font-family", "Urbanist, sans-serif")
                                property("line-height", "1.5")
                            }
                    )
                }

                // Team Name field — Figma 8622:386609
                Column(modifier = Modifier.fillMaxWidth().sGap(6)) {
                    SpanText(
                        t.eventTeamNameLabel,
                        modifier = Modifier
                            .sPaddingLeft(4)
                            .sFontSize(14)
                            .sLineHeight(22)
                            .sLetterSpacing(0.028)
                            .color(Color("var(--arena-text-2)"))
                            .styleModifier {
                                property("font-family", "Urbanist, sans-serif")
                            }
                    )
                    TeamNameInput(
                        value = name,
                        onValueChange = { name = it; nameError = null },
                        hasError = nameError != null,
                    )
                    nameError?.let {
                        SpanText(
                            it,
                            modifier = Modifier
                                .sPaddingLeft(4)
                                .sFontSize(12)
                                .color(Color("#FF6B6B"))
                                .styleModifier {
                                    property("font-family", "Urbanist, sans-serif")
                                }
                        )
                    }
                }
            }

            // ── Team Color ────────────────────────────────────────────────
            Column(modifier = Modifier.sGap(18)) {
                Column(modifier = Modifier.sGap(6)) {
                    SpanText(
                        t.eventTeamColorLabel,
                        modifier = Modifier
                            .sPaddingLeft(4)
                            .sFontSize(16)
                            .sLetterSpacing(0.16)
                            .color(Color("var(--arena-text-2)"))
                            .styleModifier {
                                property("font-family", "Urbanist, sans-serif")
                                property("line-height", "1.5")
                            }
                    )
                    SpanText(
                        t.eventTeamColorSubtitle,
                        modifier = Modifier
                            .sPaddingLeft(4)
                            .sFontSize(12)
                            .sLineHeight(12)
                            .sLetterSpacing(0.012)
                            .color(Color("var(--arena-text-muted)"))
                            .styleModifier {
                                property("font-family", "Urbanist, sans-serif")
                            }
                    )
                }

                // Color swatches row — Figma 8622:386621
                Row(modifier = Modifier.sGap(12)) {
                    availableColors.forEach { hex ->
                        ColorSwatch(
                            color = hex,
                            isSelected = selectedColor == hex,
                            onClick = {
                                selectedColor = if (selectedColor == hex) null else hex
                            }
                        )
                    }
                }
            }

            // ── Action buttons ────────────────────────────────────────────
            Row(
                modifier = Modifier.fillMaxWidth().sGap(12),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                PillButton(
                    text = t.commonCancel,
                    onClick = { onClose() },
                    background = AppGradients.InteractiveSecondary,
                    textColor = "#BED6F5",
                )
                PillButton(
                    text = t.eventCreateTeam,
                    onClick = {
                        if (name.isBlank()) {
                            nameError = t.eventTeamNameRequired
                            return@PillButton
                        }
                        onCreated(name.trim(), selectedColor)
                    },
                    background = "#0061FF",
                    textColor = "#FFFFFF",
                )
            }
        }
    }
}

@Composable
private fun TeamNameInput(
    value: String,
    onValueChange: (String) -> Unit,
    hasError: Boolean,
) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(44)
            .sBorderRadius(28)
            .sPaddingLeft(12)
            .sPaddingRight(8)
            .styleModifier {
                property("border", "0.3px solid transparent")
                val borderLayer = if (hasError) {
                    "linear-gradient(180deg, rgba(255, 107, 107, 0.7), rgba(255, 107, 107, 0.3))"
                } else AppGradients.ButtonBorderGradientPrimary
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(var(--l-s2, #09142D), var(--l-s2, #09142D)) padding-box, " +
                        "$borderLayer border-box"
                )
                property("display", "flex")
                property("align-items", "center")
                property("box-sizing", "border-box")
            },
        contentAlignment = Alignment.CenterStart,
    ) {
        Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
            value(value)
            onInput { onValueChange(it.value) }
            placeholder(t.eventTeamNamePlaceholder)
            style {
                property("width", "100%")
                property("height", "100%")
                property("background", "transparent")
                property("border", "none")
                property("outline", "none")
                property("color", "#FFFFFF")
                property("font-family", "Urbanist, sans-serif")
                property("font-size", s(16))
                property("line-height", "1.5")
                property("letter-spacing", "0.16px")
                property("padding", "0")
                // Placeholder colour comes from the Figma muted token
                // (rgba(153,177,191,0.5)); applied via a class below.
            }
            // Inline class for placeholder colour — using inline <style> is
            // unreliable in compose-web, so we set the class and inject the
            // rule once via a global style block on first render.
            classes("pp-team-name-input")
        }
    }
    // Inject placeholder styling once.
    LaunchedEffect(Unit) {
        val styleId = "pp-team-name-input-style"
        if (document.getElementById(styleId) == null) {
            val styleEl = document.createElement("style")
            styleEl.id = styleId
            styleEl.textContent = """
                .pp-team-name-input::placeholder {
                  color: rgba(153, 177, 191, 0.5);
                  opacity: 1;
                }
            """.trimIndent()
            document.head?.appendChild(styleEl)
        }
    }
}

@Composable
private fun ColorSwatch(
    color: String,
    isSelected: Boolean,
    onClick: () -> Unit,
) {
    // Outer wrapper is NOT clipped so the selection badge can overhang the
    // top-right corner; the coloured circle below clips its own texture/glow.
    Box(
        modifier = Modifier
            .sSize(45)
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
    ) {
        Box(
            modifier = Modifier
                .sSize(45)
                .sBorderRadius(100)
                .overflow(Overflow.Hidden)
                .position(Position.Relative)
                .styleModifier {
                    property("background-color", color)
                    property("border", "0.5px solid rgba(13, 24, 74, 0.6)")
                },
            contentAlignment = Alignment.Center,
        ) {
            // Team card texture — masked into the circle.
            Image(
                src = "/images/team-card-texture.png",
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(0.px).left(0.px)
                    .fillMaxSize()
                    .styleModifier {
                        property("opacity", "0.55")
                        property("object-fit", "cover")
                        property("pointer-events", "none")
                    }
            )
            // Team card glow.
            Image(
                src = "/images/team-card-glow.svg",
                modifier = Modifier
                    .position(Position.Absolute)
                    .styleModifier {
                        property("width", s(86))
                        property("height", s(86))
                        property("left", s(-20))
                        property("top", s(-36))
                        property("pointer-events", "none")
                    }
            )
            // Team card deco.
            Image(
                src = "/images/team-card-deco.svg",
                modifier = Modifier
                    .position(Position.Absolute)
                    .styleModifier {
                        property("width", s(130))
                        property("height", s(130))
                        property("left", "75%")
                        property("top", s(-50))
                        property("transform", "translateX(-8px) scaleY(-1) rotate(180deg)")
                        property("pointer-events", "none")
                    }
            )
            // 3-person silhouette per Figma — ic_team_people.svg has the same
            // 6-vector layout (left+centre+right figure). Forced to black @ 0.5
            // so it reads as a dark silhouette over the colored circle.
            Icon(
                src = AppIcons.TeamPeople,
                modifier = Modifier
                    .sWidth(22).sHeight(15)
                    .styleModifier {
                        property("position", "relative")
                        property("z-index", "1")
                        property("filter", "brightness(0) saturate(100%)")
                        property("opacity", "0.5")
                        property("pointer-events", "none")
                    }
            )
        }

        // Selected → small blue check badge at the top-right (Figma 8854:356317).
        if (isSelected) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .zIndex(2)
                    .sSize(16)
                    .sBorderRadius(100)
                    .styleModifier {
                        property("top", "0")
                        property("right", s(-1))
                        property("background-color", "#0061FF")
                        property("border", "0.5px solid var(--l-s2, #09142D)")
                    },
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    src = AppIcons.Check,
                    modifier = Modifier.sSize(9).styleModifier {
                        property("filter", "brightness(0) invert(1)")
                    },
                )
            }
        }
    }
}

@Composable
private fun PillButton(
    text: String,
    onClick: () -> Unit,
    background: String,
    textColor: String,
    fullWidth: Boolean = false,
) {
    // Gradient border via the padding-box / border-box overlay. Solid
    // colours have to be wrapped in linear-gradient() because the
    // `background:` shorthand only accepts <image> values per layer.
    val fgLayer = if (background.startsWith("linear-gradient")) {
        "$background padding-box, linear-gradient(var(--l-s2, #09142D), var(--l-s2, #09142D)) padding-box"
    } else {
        "linear-gradient($background, $background) padding-box"
    }
    Box(
        modifier = Modifier
            .then(if (fullWidth) Modifier.fillMaxWidth() else Modifier)
            .sHeight(44)
            .sBorderRadius(100)
            .sPadding(13, 28)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "$fgLayer, ${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("box-sizing", "border-box")
                property("white-space", "nowrap")
            },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(16)
                .sLineHeight(18)
                .sLetterSpacing(0.032)
                .fontWeight(FontWeight.Medium)
                .color(Color(textColor))
                .styleModifier {
                    property("font-family", "Urbanist, sans-serif")
                }
        )
    }
}
